import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { formatDate, formatFileNameProduct } from 'utils/func';
import AllocatedExpenditureDialog from '../AllocatedExpenditureDialog';

export default function SubAllocatedExpenditureTable({
  disabled,
  control,
  projectCode,
  setExpenditurePeriodsDelete,
  handleDownloadFile,
}) {
  // #region Data
  const { t } = useTranslation();
  const allocatedExpenditureDialogRef = useRef(null);

  const { fields, insert, update, remove } = useFieldArray({
    control,
    name: 'summaryReportAllocatedExpenditures',
    keyName: 'customId',
  });

  // #endregion Data
  return (
    <div className="form-table">
      <table className="mb-2">
        <thead>
          <tr>
            <th className="text-center" style={{ minWidth: 20, width: 20 }}>
              {t('formLayout.numericalOrder')}
            </th>
            <th className="text-center" style={{ minWidth: 100, width: 120 }}>
              {t('r05.a.a5.table.decisionNo')}
            </th>
            <th className="text-center" style={{ minWidth: 100, width: 120 }}>
              {t('r05.a.a5.table.issueDate')}
            </th>
            <th className="text-center" style={{ minWidth: 110, width: 120 }}>
              {t('r05.a.a5.table.amount')}
            </th>
            <th className="text-center" style={{ minWidth: 160, width: 180 }}>
              {t('r05.b.b2.table.proof')}
            </th>
            {!disabled && (
              <th className="text-center float" style={{ minWidth: 40, width: 40 }}>
                {t('projectProposal.action')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {fields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>{item?.decisionNo}</td>
              <td>{item?.issueDate ? formatDate(item?.issueDate, 'dd/MM/yyyy') : ''}</td>
              <td className="text-right">
                {item?.amount?.toLocaleString('vi-VN', {
                  style: 'currency',
                  currency: 'VND',
                })}
              </td>
              <td>
                {!item?.file?.name ? (
                  item?.filename && (
                    <span
                      className="p-button p-button-text p-button-sm mt-1 p-0"
                      onClick={() => {
                        handleDownloadFile(
                          item?.filename,
                          formatFileNameProduct(item?.filename || '', projectCode, 'KP', -1),
                          'summary-report-allocated-expenditures'
                        );
                      }}
                    >
                      <i
                        className={`pi text-xl ${
                          item?.filename?.endsWith('.pdf')
                            ? 'pi-file-pdf text-red-500'
                            : 'pi-file-word'
                        }`}
                      />{' '}
                      <span className="text-blue-500 underline font-italic ml-1">
                        {formatFileNameProduct(item?.filename || '', projectCode, 'KP', index + 1)}
                      </span>
                    </span>
                  )
                ) : (
                  <span>
                    {formatFileNameProduct(item?.file?.name || '', projectCode, 'KP', index + 1)}
                  </span>
                )}
              </td>
              {!disabled && (
                <td className="float text-center p-0">
                  <div>
                    <i
                      onClick={() => {
                        allocatedExpenditureDialogRef.current.open({
                          index,
                          projectCode,
                          value: fields[index],
                          update,
                          type: 'UPDATE',
                        });
                      }}
                      className="edit-icon pi pi-pencil text-primary cursor-pointer mt-1"
                      data-pr-tooltip={t('formLayout.action.edit')}
                      data-pr-position="left"
                    />
                    <Tooltip target=".edit-icon" />
                  </div>

                  <i
                    onClick={() => {
                      if (item?.id) {
                        setExpenditurePeriodsDelete((prev) => [...prev, item.id]);
                      }
                      remove(index);
                    }}
                    className="remove-icon pi pi-times text-red-700 cursor-pointer mb-1"
                    data-pr-tooltip={t('formLayout.action.remove')}
                    data-pr-position="left"
                  />
                  <Tooltip target=".remove-icon" />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {!disabled && (
        <div className="flex justify-content-center my-2">
          <i
            onClick={() => {
              allocatedExpenditureDialogRef.current.open({
                insert,
                projectCode,
                index: fields.length || 0,
                type: 'INSERT',
              });
            }}
            className="add-recommended-expert pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle"
            data-pr-tooltip={t('formLayout.action.add')}
            data-pr-position="left"
          />
          <Tooltip target=".add-recommended-expert" />
        </div>
      )}

      <AllocatedExpenditureDialog
        ref={allocatedExpenditureDialogRef}
        handleDownloadFile={handleDownloadFile}
        productTypeShort="KP"
      />
    </div>
  );
}

SubAllocatedExpenditureTable.propTypes = {
  disabled: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  projectCode: PropTypes.string,
  setExpenditurePeriodsDelete: PropTypes.func,
  handleDownloadFile: PropTypes.func,
};

SubAllocatedExpenditureTable.defaultProps = {
  disabled: false,
  projectCode: '',
  setExpenditurePeriodsDelete: () => {},
  handleDownloadFile: () => {},
};
